import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  HelpCircle,
  KeyRound,
  LogOut,
  Megaphone,
  Search,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.js';
import { Btn } from '../../components/ui.js';
import { formatDatum } from '../../lib/format.js';
import { useMojeNalezy } from '../../lib/mojeNalezy.js';
import { ZmenaHeslaDialog } from '../../components/ZmenaHesla.js';
import { odeberZFronty, synchronizuj, useOfflineFronta } from '../../lib/offline.js';
import { resetUvod } from './OnboardingKarta.js';

/**
 * Terénní menu — strukturované sekce s řádky (ne hromada stejných tlačítek):
 * profil, Moje agenda (nálezy/výzvy/nápověda), Odesílání (offline fronta
 * vč. konfliktů), Účet. Řádky ≥56 px (palec v rukavici), šipka = kam vede.
 */

/** Skupina řádků menu: popisek nad kartou, řádky oddělené linkou. */
function Skupina({ nazev, children }: { nazev: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="px-4 pb-1.5 text-xs font-semibold tracking-wider text-text-secondary uppercase">
        {nazev}
      </p>
      <div className="overflow-hidden rounded-xl border border-border bg-surface">{children}</div>
    </section>
  );
}

const RADEK_VZHLED =
  'flex min-h-14 w-full items-center gap-3 border-t border-border px-4 text-left text-base font-semibold text-text-primary transition-colors duration-100 first:border-t-0 hover:bg-bg active:bg-primary-tint';

/** Řádek menu s ikonou, textem, volitelným badge a šipkou. */
function Radek({
  Ikona,
  text,
  badge,
  to,
  onClick,
}: {
  Ikona: typeof Search;
  text: string;
  badge?: number;
  to?: string;
  onClick?: () => void;
}) {
  const obsah = (
    <>
      <Ikona size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-primary" />
      <span className="min-w-0 flex-1 truncate">{text}</span>
      {badge != null && badge > 0 && (
        <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-error-bg px-2 py-0.5 text-sm font-bold text-error-text">
          {badge}
        </span>
      )}
      <ChevronRight
        size={18}
        strokeWidth={2}
        aria-hidden="true"
        className="shrink-0 text-text-muted"
      />
    </>
  );
  if (to) {
    return (
      <Link to={to} className={RADEK_VZHLED}>
        {obsah}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={RADEK_VZHLED}>
      {obsah}
    </button>
  );
}

export function MenuPage() {
  const [menimHeslo, setMenimHeslo] = useState(false);
  const { me, maPravo, logout } = useAuth();
  const navigate = useNavigate();
  const fronta = useOfflineFronta();
  const { noveZamitnute } = useMojeNalezy(!!me);

  const inicialy = (me?.jmeno ?? '')
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-xl font-bold">Menu</h1>

      {/* Profil — kdo jsem. */}
      <section className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-tint text-sm font-bold text-primary">
          {inicialy}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-base font-semibold">{me?.jmeno}</span>
          <span className="block truncate text-sm text-text-secondary">{me?.role.join(', ')}</span>
        </span>
      </section>

      <Skupina nazev="Moje agenda">
        <Radek Ikona={CalendarClock} text="Docházka party" to="/teren/dochazka" />
        <Radek
          Ikona={AlertTriangle}
          text="Moje nálezy"
          badge={noveZamitnute}
          to="/teren/moje-nalezy"
        />
        {/* Výzvy ustoupily v liště Výkresům (zakládají se hlavně z deníku). */}
        <Radek Ikona={Megaphone} text="Výzvy" to="/teren/vyzvy" />
        <Radek
          Ikona={HelpCircle}
          text="Jak to funguje"
          onClick={() => {
            resetUvod();
            void navigate('/teren/denik');
          }}
        />
      </Skupina>

      <Skupina nazev="Odesílání">
        <div className="flex min-h-14 items-center gap-3 px-4 py-3">
          {fronta.ceka === 0 && fronta.konflikty.length === 0 ? (
            <>
              <CheckCircle2
                size={20}
                strokeWidth={1.75}
                aria-hidden="true"
                className="shrink-0 text-success-text"
              />
              <span className="flex-1 text-base font-semibold text-success-text">
                Vše odesláno.
              </span>
            </>
          ) : (
            <>
              <CloudUpload
                size={20}
                strokeWidth={1.75}
                aria-hidden="true"
                className="shrink-0 text-warning-text"
              />
              <span className="min-w-0 flex-1 text-base font-semibold">
                Čeká na odeslání: {fronta.ceka}
              </span>
              {fronta.ceka > 0 && (
                <Btn
                  disabled={fronta.synchronizuji || !fronta.online}
                  onClick={() => void synchronizuj()}
                >
                  {fronta.synchronizuji ? 'Odesílám…' : 'Odeslat teď'}
                </Btn>
              )}
            </>
          )}
        </div>
        {fronta.konflikty.map((k) => (
          <div key={k.id} className="border-t border-border px-4 py-3">
            <p className="text-base font-semibold">{k.popis}</p>
            <p className="text-sm text-error-text">
              {formatDatum(k.kdy)} — nepodařilo se uložit: {k.konflikt}
            </p>
            <p className="text-sm text-text-primary">
              Zapište to prosím znovu ručně (např. opravným záznamem) a položku odeberte.
            </p>
            <button
              type="button"
              className="mt-1 min-h-12 font-semibold text-error-text underline underline-offset-2"
              onClick={() => void odeberZFronty(k.id)}
            >
              Odebrat z fronty
            </button>
          </div>
        ))}
      </Skupina>

      <Skupina nazev="Účet">
        {maPravo('projekty.read') && (
          <Radek Ikona={Building2} text="Přepnout do kanceláře" to="/zakazky" />
        )}
        <Radek Ikona={KeyRound} text="Změnit heslo" onClick={() => setMenimHeslo(true)} />
        <Radek Ikona={LogOut} text="Odhlásit se" onClick={() => void logout()} />
      </Skupina>

      {menimHeslo && <ZmenaHeslaDialog onZavrit={() => setMenimHeslo(false)} />}
    </div>
  );
}
